import React from 'react'
import PropTypes from 'prop-types'
import { useSetState } from 'react-use'
import { flowRight } from 'lodash'
import * as Nope from 'lib/utils/validation/Nope'
import * as Yup from 'yup'
import Vehicle from 'lib/utils/store/wrappers/Vehicle'
import { withTranslationCustom } from 'lib/i18next'
import useValidate from 'lib/hooks/useValidate'

import ErrorComponent from 'components/Errors/ErrorComponent'
import Button from 'components/UI/Button'
import { Form } from 'formik'
import VehicleProfileLogic from 'components/Vehicles/VehicleProfile/VehicleProfileLogic'
import ExtendedFormik from 'components/Forms/ExtendedFormik'
import FormBuilder from 'components/Forms/FormBuilder/FormBuilder'
import LoadingIndicator from 'components/LoadingIndicator'

const VehicleDataEdit = ({
    t,
    children,
    wrappedVehicle,
    fieldOptions,
    onSubmitted,
}) => {
    const fieldNames = fieldOptions.map(field => field.name)
    const fieldsSchema = {
        fields: wrappedVehicle.getFieldsForFieldSchema(t, fieldNames),
    }

    const fieldValidations = Vehicle.getFieldValidations(fieldsSchema.fields)

    // mark field validation required based on field options
    Object.keys(fieldValidations).forEach(key => {
        if (fieldOptions.find(field => field.name === key).required) {
            fieldValidations[key] = fieldValidations[key].required('formElements:validation.required')
        }
    })

    const validationSchema = Nope.schema({
        vehicleType: Yup.string(),
        ...fieldValidations,
    })

    const [state, setState] = useSetState({
        initialValues: {
            vehicleType: wrappedVehicle.getVehicleTypeId() || '',
            ...Vehicle.getFieldInitialValues(fieldsSchema.fields),
        },
        completed: false,
    })

    const { initialValues, completed } = state

    const [validating, isValid] = useValidate(
        validationSchema,
        initialValues,
    )

    if (validating) return <LoadingIndicator />

    const renderDataForm = () => (
        <VehicleProfileLogic>
            {({ updateVehicle, error }) => (
                <ExtendedFormik
                    id="vehicle-data-edit"
                    validationSchema={validationSchema}
                    initialValues={initialValues}
                    onSubmit={async (formValues, formActions) => {
                        const vehicleInput = {
                            ...formValues,
                            vehicleType: parseInt(formValues.vehicleType),
                            mfgYear: String(formValues.mfgYear),
                        }

                        await updateVehicle(wrappedVehicle.getId(), vehicleInput)
                            .then(() => {
                                onSubmitted && onSubmitted()
                                setState({ completed: true })
                                formActions.setSubmitting(false)
                            })
                            .catch(() => {
                                formActions.setSubmitting(false)
                            })
                    }}
                    enableReinitialize={true}
                >
                    {(form) => {
                        const { isSubmitting } = form
                        return (
                            <Form noValidate>
                                <FormBuilder
                                    form={form}
                                    fieldsSchema={fieldsSchema}
                                    fieldsLayoutSchema={wrappedVehicle.getPrimaryDetailsFieldsLayoutSchema()}
                                    fieldsData={{}}
                                    builderValues={{
                                        vehicleTypeId: wrappedVehicle.getVehicleTypeId(),
                                    }}
                                    shouldRenderSearchSelect
                                >
                                    {({ fields, renderFieldsWithLayout }) => {
                                        return renderFieldsWithLayout(fields)
                                    }}
                                </FormBuilder>
                                {error && <ErrorComponent error={error} />}
                                <div className="action-wrapper">
                                    <Button
                                        className="button"
                                        type="submit"
                                        disabled={isSubmitting}
                                        loading={isSubmitting}
                                        trackName="submit"
                                    >
                                        {t('common:continue')}
                                    </Button>
                                </div>
                            </Form>
                        )
                    }}
                </ExtendedFormik>
            )}
        </VehicleProfileLogic>
    )

    return children({
        isValid,
        renderDataForm: renderDataForm,
        completed,
    })
}

VehicleDataEdit.propTypes = {
    t: PropTypes.func.isRequired,
    children: PropTypes.func.isRequired,
    wrappedVehicle: PropTypes.object.isRequired,
    onSubmitted: PropTypes.func,
    fieldOptions: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        required: PropTypes.bool.isRequired,
    })).isRequired,
}

export default flowRight(
    withTranslationCustom(['formElements', 'vehicleProfile']),
)(VehicleDataEdit)
